import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BenchmarkDataPrimaryLookupService,
  BmarkClientOrdersTableDefinitionFactory,
  BmarkFixingData,
  BmarkFixingType,
  BmarkOrderDataOperation,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BmarkFixingsManagerService } from "app/bfx-bmark-summary/services/bmark-fixings-manager.service";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import {
  BmarkOrderDataEvent,
  BmarkOrderDataEventPayload,
} from "shared/cubiq-services/model/benchmarks/BmarkOrderDataEvent.model";
import { BmarkClientOrdersBlotterMode } from "shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import { BfxBaseBlotterService } from "../../../../bfx-base-blotter/bfx-base-blotter.service";

import {
  BfxBlotterFilter,
  BfxBlotterFilterId,
  ExtendedOrderTypeIdentifier,
  FilterType,
} from "app/bfx-blotter-filter/bfx-blotter-filter.constants";
import { BmarkSummaryViewFilterType } from "app/bfx-bmark-summary/models/bmark-summary-view-filter-type.enum";
import { OELoggerService } from "app/order-entry/services/basic/oe-logger.service";
import { filter } from "rxjs";
import {
  BLOTTER_RELOAD_TYPE,
  BmarkRiskOrderDataProvider,
  RiskBMOrderDataListener,
  RiskSubscriptionType,
  RiskModelDataListener,
} from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";
import { BfxBlotterFilterService } from "./../../../../bfx-blotter-filter/bfx-blotter-filter.service";
import {
  BmarkSummaryUtilService,
  FilterAppliedStatusModel,
} from "./../../../services/bmark-summary-util.service";
import { BmarkClientOrdersFilterService } from "./bmark-client-orders-filter.service";

/**
 * Maintains model of currently displayed orders in active fixing - as orders are added/modified/deleted
 * this service will respond to the corresponding events, and adjust the display model accordingly
 */
@Injectable()
export class BmarkClientOrdersDataService
  extends BfxBaseBlotterService
  implements BmarkRiskOrderDataProvider, RiskModelDataListener
{
  private selectedFixingData: BmarkFixingData;
  private selectedFixingId: string;
  private selectedFixingType: BmarkFixingType = null;
  private blotterMode: BmarkClientOrdersBlotterMode;
  public filterAppliedObj: FilterAppliedStatusModel = new FilterAppliedStatusModel(false, []);

  constructor(
    protected oeLogger: OELoggerService,
    public dataService: BenchmarkDataPrimaryLookupService,
    public tableDefinitionService: BmarkClientOrdersTableDefinitionFactory,
    private bmarkManager: BmarkFixingsManagerService,
    protected bmarkSummaryUtilService: BmarkSummaryUtilService,
    protected blotterFilterService: BfxBlotterFilterService,
    protected filterService: BmarkClientOrdersFilterService,
  ) {
    super(dataService, tableDefinitionService);
    this.oeLogger = this.oeLogger.for(__dirname);
    this.initialize();
  }

  private initialize() {
    this.dataService.isPrimed().then((_primed: boolean) => {
      // Fixing Manager
      this.bmarkManager.displayFixing$.subscribe(this.onFixingManagerEvent.bind(this));
      // Fixing Lookup
      this.dataService
        .getBmarkOrderDataUpdateSubject()
        .subscribe(this.onBmarkOrderDataEvent.bind(this));
    });
    this.bmarkSummaryUtilService.filterAppliedObs$
      .pipe(filter((val) => val != null))
      .subscribe(
        (filterAppliedObj: FilterAppliedStatusModel) => (this.filterAppliedObj = filterAppliedObj),
      );
  }

  /*************************************/
  /* Event handlers */

  protected onFixingManagerEvent(fixingData: BmarkFixingData) {
    logger.out.info(
      `bmcod-s - Received BmarkFixingManagerEvent [${
        fixingData instanceof BmarkFixingData ? fixingData.id : "NULL"
      }]`,
    );
    let fixingChanged: boolean = false;
    if (fixingData instanceof BmarkFixingData) {
      fixingChanged =
        this.selectedFixingId !== fixingData.id || this.selectedFixingType !== fixingData.type;
      this.selectedFixingData = fixingData;
      this.selectedFixingId = fixingData.id;
      this.selectedFixingType = fixingData.type;
    } else {
      fixingChanged = this.selectedFixingId !== "";
      this.selectedFixingData = null;
      this.selectedFixingId = "";
      this.selectedFixingType = null;
    }

    logger.out.info(
      `bmcod-s - Active fixing ${fixingChanged ? "CHANGED" : "SAME"} -> ${
        fixingData instanceof BmarkFixingData ? fixingData.id : "NULL"
      }`,
    );
    if (fixingChanged || fixingData.forceReload) {
      this.tableDataReload(BLOTTER_RELOAD_TYPE.FIXING_CHANGE);
    }
  }

  protected onBmarkOrderDataEvent($event: BmarkOrderDataEvent): void {
    if (
      $event instanceof BmarkOrderDataEvent &&
      Array.isArray($event.operations) &&
      $event.operations.length > 0 &&
      $event.payload instanceof BmarkOrderDataEventPayload &&
      ((this.selectedFixingType == BmarkFixingType.ACTIVE &&
        typeof $event.payload.activeFixingId === "string") ||
        (this.selectedFixingType == BmarkFixingType.RECENT &&
          typeof $event.payload.recentFixingId === "string"))
    ) {
      let hasUpdate =
        $event.operations.includes(BmarkOrderDataOperation.UPDATE) ||
        $event.operations.includes(BmarkOrderDataOperation.NETTING_UPDATE);
      let hasDelete = $event.operations.includes(BmarkOrderDataOperation.DELETE);
      let matchType =
        $event.payload.activeFixingId === this.selectedFixingId
          ? "MATCHED ACTIVE"
          : $event.payload.recentFixingId === this.selectedFixingId
            ? "MATCHED RECENT"
            : "IGNORE";
      logger.out.info(
        `bmcod-s - Received BmarkOrderDataEvent (${matchType})-> opr: ` +
          `${$event.operations.map((item) => BmarkOrderDataOperation[item]).toString()}` +
          `${
            $event.payload.bmarkOrderData != null
              ? " new state: " + CubiqTS.OrderState[$event.payload.bmarkOrderData.orderState]
              : ""
          }  => `,
        $event,
      );

      if (
        $event.payload.activeFixingId === this.selectedFixingId ||
        $event.payload.recentFixingId === this.selectedFixingId ||
        // when activeFixingId has changed but existingFixingId is still selected
        ($event.payload.existingFixingId === this.selectedFixingId && hasDelete)
      ) {
        if (hasUpdate && hasDelete) {
          logger.out.error(
            `bmcod-s - includes both BmarkOrderDataOperation.UPDATE and BmarkOrderDataOperation.DELETE`,
          );
          logger.out.error(`bmcod-s - only UPDATE will be performed`);
        }

        if (hasUpdate /* || hasDIUpdate */ && $event.payload.bmarkOrderData != null) {
          this.tableDataUpdateRow($event.payload.bmarkOrderData);
        } else if (hasDelete) {
          this.tableDataDeleteRow($event.payload.orderId);
        }
      }
    }
  }

  /*************************************/
  /* TABLE METHODS */
  public initData(): void {
    logger.out.info(`bmcod-s - Table INIT`);
    this.tableDataReload(BLOTTER_RELOAD_TYPE.INIT);
  }

  private isReloadClear(reason: BLOTTER_RELOAD_TYPE): boolean {
    return (
      this.selectedFixingData == null ||
      (BLOTTER_RELOAD_TYPE.VIEW_CHANGE !== reason &&
        BLOTTER_RELOAD_TYPE.FILTER_CHANGE !== reason &&
        BLOTTER_RELOAD_TYPE.FIXING_CHANGE !== reason)
    );
  }

  private tableDataReload(reason: BLOTTER_RELOAD_TYPE): void {
    this._rawData = Object.create(null);
    this.blotterChangeSubject.next(reason);
    logger.out.info(`bmcod-s - Table RELOAD`);

    if (this.isReloadClear(reason)) {
      this.onFullUpdateEvent([], true);
    }

    if (this.selectedFixingData instanceof BmarkFixingData) {
      const requestedFixingId = this.selectedFixingData.id;

      this.dataService
        .getBmarkOrderDataForFixingId(this.selectedFixingData.id, this.selectedFixingData.type)
        .then((orderData: Array<BmarkOrderData>) => {
          if (this.selectedFixingData?.id !== requestedFixingId) {
            logger.out.info(
              `bmcod-s - ${BLOTTER_RELOAD_TYPE[reason]} fixing has changed during request. ignoring dataset`,
            );
            return;
          }

          if (Array.isArray(orderData)) {
            this.onFullUpdateEvent(this.processOrderData(orderData));
          }
        });
    }
  }

  protected tableDataDeleteRow(orderId: string): void {
    if (typeof orderId === "string") {
      logger.out.info(`bmcod-s - Table DELETE ROW: ${orderId}`);
      this.onDeleteEvent(orderId);
    } else {
      logger.out.error(`bmcod-s - Invalid id `, orderId);
    }
  }

  protected tableDataUpdateRow(orderData: BmarkOrderData): void {
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      const orderIdStr = orderData.orderId.toString();
      const state = CubiqTS.OrderState[orderData.orderState];
      if (
        orderData.allocationData != null &&
        orderData.allocationData.length > 0 &&
        this.blotterMode == BmarkClientOrdersBlotterMode.RISK
      ) {
        // Remove existing spawned risks
        logger.out.info(
          `bmcod-s - tableDataUpdateRow(): removing spawned legs (prior to update) for ${orderIdStr}`,
        );
        this.onDeleteEvent(orderIdStr);
      } else {
      }
      logger.out.info(
        `bmcod-s - tableDataUpdateRow(): Full order update for ${orderIdStr} -> ${state}`,
      );

      this.onFullUpdateEvent(this.processOrderData(orderData));
    }
  }

  protected onDeleteEvent(orderId: string): void {
    let deletedOrders: CubiqTS.OrderData[] = [];
    this.getRowData()
      .filter((order: CubiqTS.OrderDataImpl) => order.orderId.toString() == orderId)
      .forEach((element: CubiqTS.OrderDataImpl) => {
        if (element.allocationData != null && element.allocationData.length > 0) {
          let rowId = this.getRowKey(element);
          let deletedOrder = this._rawData[rowId];

          try {
            if (deletedOrder == null && Object.values(this._rawData).includes(element)) {
              let id = Object.keys(this._rawData).find((k) => this._rawData[k] === element);
              if (id != null) {
                logger.out.info(
                  `bmcod-s - onDeleteEvent(): no order found for rowID ${rowId} - using internal id ${id}`,
                );
                rowId = id;
                deletedOrder = element;
              } else {
                logger.out.info(`bmcod-s - onDeleteEvent(): no order found for rowID ${rowId}`);
              }
            }
          } catch (e) {
            logger.out.error(
              `bmcod-s - Failed to determine key for cancelled` + ` order ${rowId}, ${rowId}: ${e}`,
            );
          }

          if (deletedOrder) {
            deletedOrders.push(deletedOrder);
            delete this._rawData[rowId];
          }
        }
      });

    if (deletedOrders.length > 0) {
      logger.out.info(
        `bmcod-s - onDeleteEvent(): notifyiong deletion of ${deletedOrders.length} orders`,
      );
      deletedOrders.forEach((o) => this.deleteItemSubject.next(o));
    }
  }

  public getItemCount(): number {
    return this.getRowData().length;
  }

  public getActiveFixing(): BmarkFixingData {
    return this.selectedFixingData;
  }

  public getBlotterMode(): BmarkClientOrdersBlotterMode {
    return this.blotterMode;
  }

  /*************************************/
  /* SPAWNED DATA */
  public setBlotterMode(tableViewType): void {
    if (tableViewType in BmarkClientOrdersBlotterMode) {
      if (tableViewType !== this.blotterMode) {
        this.blotterMode = tableViewType;
        this.tableDataReload(BLOTTER_RELOAD_TYPE.VIEW_CHANGE);
      }
    }
  }

  /**
   * Transform input OrderData into array of one or more component/dealt orders.
   * @param orderData
   * @param blotterMode
   * @returns
   */
  public processOrderData(
    orderData: Array<BmarkOrderData> | BmarkOrderData,
    blotterMode?: BmarkClientOrdersBlotterMode,
  ): Array<BmarkOrderData> {
    let result: BmarkOrderData[] = [];

    let orderDataset = new Array<BmarkOrderData>(); // input array
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      orderDataset.push(orderData);
    } else {
      orderDataset = orderData;
    }

    if (blotterMode == null) {
      blotterMode = this.blotterMode;
    }

    if (Array.isArray(orderDataset) && orderDataset.length > 0) {
      if (blotterMode === BmarkClientOrdersBlotterMode.RISK) {
        result = this.generateSpawnedOrderData(orderDataset);
      } else {
        for (let orderDataItem of orderDataset) {
          // Note: this modifies the input OrderData
          orderDataItem.blotterMode = BmarkClientOrdersBlotterMode.DEALT;
          result.push(orderDataItem);
        }
      }
      result.forEach((order) => (order.primaryKey = this.getRowKey(order)));
    }
    return result;
  }

  public generateSpawnedOrderData(orderDataset: Array<BmarkOrderData>): Array<BmarkOrderData> {
    let result = new Array<BmarkOrderData>();
    if (Array.isArray(orderDataset) && orderDataset.length > 0) {
      for (let orderDataItem of orderDataset) {
        let allocationDataset: Set<CubiqTS.AllocationData> =
          this.dataService.getAllAllocationDatasetForOrder(orderDataItem);
        let isAllCrossComponent = [...allocationDataset].every(
          (allocationData) =>
            allocationData.allocationType === CubiqTS.AllocationType.CROSS ||
            allocationData.allocationType === CubiqTS.AllocationType.COMPONENT,
        );

        if (
          orderDataItem instanceof CubiqTS.OrderDataImpl &&
          Array.isArray(orderDataItem.allocationData) &&
          orderDataItem.allocationData.length > 0 &&
          isAllCrossComponent
        ) {
          // Partition into risk sets first
          const pairMap = new Map<string, CubiqTS.AllocationData[]>();
          for (let allocationData of allocationDataset) {
            if (
              !orderDataItem.spawned ||
              allocationData.allocationType === CubiqTS.AllocationType.COMPONENT
            ) {
              // First partition all allocations into pair sets
              const pairName = allocationData.baseCcy + allocationData.nonbaseCcy;
              const list = pairMap.get(pairName);
              if (list) {
                list.push(allocationData);
              } else {
                pairMap.set(pairName, [allocationData]);
              }
            }
          }
          for (let allocationList of pairMap.values()) {
            let spawnedOrderData = this.createSpawnedOrder(orderDataItem, allocationList);
            result.push(spawnedOrderData);
          }
        } else {
          let bulkOrderData = BmarkOrderData.clone(orderDataItem);
          bulkOrderData.blotterMode = BmarkClientOrdersBlotterMode.RISK;
          result.push(bulkOrderData);
        }
      }
    }
    return result;
  }

  /**
   * Create a BMarkOrderData from existing order and new set of allocations
   * @param orderDataItem
   * @param allocations
   * @returns
   */
  private createSpawnedOrder(
    orderDataItem: BmarkOrderData,
    allocations: CubiqTS.AllocationData[],
  ): BmarkOrderData {
    let spawnedOrderData = BmarkOrderData.clone(orderDataItem);
    spawnedOrderData.allocationData = allocations;
    spawnedOrderData.blotterMode = BmarkClientOrdersBlotterMode.RISK;

    return spawnedOrderData;
  }

  onFilterApplied() {
    this.blotterChangeSubject.next(BLOTTER_RELOAD_TYPE.FILTER_CHANGE);
  }

  subscribe(type: RiskSubscriptionType, listener: RiskBMOrderDataListener) {
    if (type === RiskSubscriptionType.CLIENT) {
      this.blotterChangeSubject.subscribe((reason: BLOTTER_RELOAD_TYPE) => {
        try {
          listener.onBlotterReload(reason, RiskSubscriptionType.CLIENT);
        } catch (ex) {
          logger.out.error("bmcod-s - Error in BlotterChangeSubject Handler " + ex);
        }
      });

      this.deleteItemSubject.subscribe((row: any) => {
        try {
          let param: Set<BmarkOrderData> = new Set<BmarkOrderData>();
          param.add(row);
          listener.onRemoveOrders(param, RiskSubscriptionType.CLIENT, false);
        } catch (ex) {
          logger.out.error("bmcod-s - Error in deleteItemSubject Handler " + ex);
        }
      });

      this.bmarkSummaryUtilService.filterAppliedObs$.subscribe(
        (filterAppliedObj: FilterAppliedStatusModel) => {
          if (this.selectedFixingData != null) {
            logger.out.info(
              "bmcod-s - Retrieving BMark Order Data after filter applied for fixingID " +
                this.selectedFixingData.id,
            );
            const requestedFixingId = this.selectedFixingData.id;
            this.dataService
              .getBmarkOrderDataForFixingId(
                this.selectedFixingData.id,
                this.selectedFixingData.type,
              )
              .then((orderData: Array<BmarkOrderData>) => {
                if (this.selectedFixingData?.id !== requestedFixingId) {
                  logger.out.info(
                    `bmcod-s - subscribe() FilterAppliedStatusModel fixing has changed during request. ignoring dataset`,
                  );
                  return;
                }

                try {
                  if (Array.isArray(orderData)) {
                    if (
                      filterAppliedObj != null &&
                      (filterAppliedObj.isFilterApplied ||
                        filterAppliedObj.viewFilterType !== BmarkSummaryViewFilterType.ALL)
                    ) {
                      this.updateOrderDataWhenFilterApplied(
                        this.processOrderData(orderData),
                        listener,
                      );
                    } else {
                      if (filterAppliedObj && !filterAppliedObj.isFilterApplied) {
                        logger.out.info(`bmcod-s - No named filters applied`);
                      }

                      this.updateOrderData(this.processOrderData(orderData), listener);
                    }
                  }
                } catch (ex) {
                  logger.out.error(
                    "bmcod-s - Error in bmarkSummaryUtilService filterAppliedObs Handler " + ex,
                  );
                }
              })
              .catch((ex) =>
                logger.out.error(
                  "bmcod-s - FilterAppliedObs$ Handler: Error in getting BmarkOrderData for FixingId " +
                    ex,
                ),
              );
          }
        },
      );

      this.fullUpdateSubject.subscribe((rows: Array<any>) => {
        try {
          if (
            this.filterAppliedObj != null &&
            (this.filterAppliedObj.isFilterApplied ||
              this.filterAppliedObj.viewFilterType !== BmarkSummaryViewFilterType.ALL)
          ) {
            this.updateOrderDataWhenFilterApplied(rows, listener);
          } else {
            this.updateOrderData(rows, listener);
          }
        } catch (ex) {
          logger.out.error("bmcod-s - Error in fullUpdateSubject Handler " + ex);
        }
      });
    }
  }

  public async getAppliedFilters(): Promise<BfxBlotterFilter[]> {
    if (this.filterAppliedObj != null && this.filterAppliedObj.isFilterApplied) {
      let filterItems: BfxBlotterFilter[] = await this.blotterFilterService.getItems(
        FilterType.BlotterFilter,
        new BfxBlotterFilterId(CubiqTS.BlotterType.ORDER, CubiqTS.AssetClass.FIX),
        ExtendedOrderTypeIdentifier.BENCHMARK,
      );

      return filterItems.filter(
        (filterItem: BfxBlotterFilter) =>
          this.filterAppliedObj.filterConfiguration.indexOf(filterItem.filterId) >= 0,
      );
    }
  }

  private async updateOrderDataWhenFilterApplied(
    rows: Array<any>,
    listener: RiskBMOrderDataListener,
  ): Promise<void> {
    let filterItems: BfxBlotterFilter[] = await this.blotterFilterService.getItems(
      FilterType.BlotterFilter,
      new BfxBlotterFilterId(CubiqTS.BlotterType.ORDER, CubiqTS.AssetClass.FIX),
      ExtendedOrderTypeIdentifier.BENCHMARK,
    );
    let selectedFilter: Array<BfxBlotterFilter> = filterItems.filter(
      (filterItem: BfxBlotterFilter) =>
        this.filterAppliedObj.filterConfiguration.indexOf(filterItem.filterId) >= 0,
    );
    if (selectedFilter.length == 0) {
      logger.out.info(`bmcod-s - clearing filters`);
    } else {
      const filterIds = selectedFilter.map((filter) => `${filter.filterName}(${filter.filterId})`);
      logger.out.info(
        `bmcod-s - applying ${selectedFilter.length} filters: [${filterIds.join(",")}]`,
      );
    }
    let filteredOutRows: Array<any> = rows.filter((row) =>
      this.filterService.doFiltersPass(row, selectedFilter),
    );
    this.updateOrderData(filteredOutRows, listener);
  }

  private updateOrderData(rows: Array<any>, listener: RiskBMOrderDataListener) {
    let param: Set<BmarkOrderData> = new Set<BmarkOrderData>();
    rows.forEach((row) => {
      if (this.bmarkSummaryUtilService.doesQuickFilterPass(row, RiskSubscriptionType.CLIENT)) {
        param.add(row);
      }
    });
    listener.onUpdateOrders(param, RiskSubscriptionType.CLIENT);
  }

  getOrderData(incoming: any, totalSet: any): Set<BmarkOrderData> {
    let ret: Set<BmarkOrderData> = new Set<BmarkOrderData>();
    for (const row of incoming) {
      let key = this.getRowKey(row);
      if (null != key) {
        let orderData: BmarkOrderData = totalSet[key];
        if (null != orderData) {
          ret.add(orderData); // take it from rawData as the data in the filter notif is not complete
        }
      }
    }
    return ret;
  }

  /**
   * Always key order data by the constant unique ID based on order ID and pair
   * @param row
   * @returns
   */
  private getRowKey(row: CubiqTS.OrderDataImpl): string {
    let ret: string;
    const orderId = row?.orderId?.toString() ?? "UNKNOWN";

    if (row.allocationData != null && row.allocationData.length > 0) {
      // All allocations here will have same pair
      const firstAllocation = row.allocationData[0];
      const pairName = firstAllocation.baseCcy + firstAllocation.nonbaseCcy;
      ret = `${orderId}-${pairName}`;
    } else {
      ret = orderId;
    }
    return ret;
  }

  primeData(): Promise<Set<BmarkOrderData>> {
    return new Promise<Set<BmarkOrderData>>((res, rej) => {
      let ret: Set<BmarkOrderData> = new Set<BmarkOrderData>();
      let innerData: Array<BmarkOrderData> = this.getRowData();
      if (innerData) {
        innerData.forEach((order) => {
          ret.add(order);
        });
      }
      res(ret);
    });
  }

  /**
   * Called when the Spot Risk Model resets
   * @param reason
   */
  onBlotterReload(reason: BLOTTER_RELOAD_TYPE) {
    //TBD
  }

  /**
   * Called when the Spot Risk Model updates 1|* pair
   * @param update
   */
  onUpdatePairs(updatedPairs: Set<string>) {
    //TBD
  }

  /**
   * Called when the Spot Risk Model removes 1|* pairs
   */
  onRemovePairs(removedPairs: Set<string>) {
    //TBD
  }
}
